import React, { useEffect, useRef, useState } from "react"
import { Box, Button, TextField, Typography } from "@mui/material"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import RemoveIcon from "@mui/icons-material/Remove"
import AddIcon from "@mui/icons-material/Add"
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined"
import SwapVertIcon from "@mui/icons-material/SwapVert"
import moment from "moment"

import LockIcon from "@mui/icons-material/Lock"

import { FRIDGE_FILTERS, isStockDeclared } from "../../utils/inventoryUtils"
import { STOCK_TYPES, TRANSFER_REFUSALS } from "../../utils/stockTypes"
import { FRIDGES } from "./fridges"

/**
 * KFC-353 — the action panel of the inventory screen, for hubs with a retail offer.
 *
 * Two tabs, opening on "inventaire". Both buttons close the panel and **write nothing**:
 * persisting the inventory is KFC-285, persisting the transfer is KFC-351. That is worth
 * knowing before testing the screen, since a button that closes without changing a stock
 * reads like a bug.
 *
 * Glyphs and colours are the ones DSG-36 specifies, already used at reception since KFC-284.
 */

/**
 * KFC-351 : un seul vocabulaire pour designer les deux frigos, celui de `STOCK_TYPES`.
 *
 * Le panneau avait le sien — "retail" / "clickAndCollect" — que l'action de transfert
 * comparait a `STOCK_TYPES.LIBRE_SERVICE`. Aucune de ces comparaisons ne pouvait etre vraie,
 * si bien que le sens du transfert etait ignore et le stock libre-service ecrase par une
 * valeur derivee du click & collect. Deux vocabulaires pour la meme chose, c'est le piege
 * lui-meme : on n'en garde qu'un.
 */
const RETAIL = STOCK_TYPES.LIBRE_SERVICE
const CLICK_AND_COLLECT = STOCK_TYPES.CLICK_AND_COLLECT

const INVENTORY_TAB = "inventaire"
const TRANSFER_TAB = "transfert"

// DSG-36 donne une icone a chaque segment : `checklist` pour l'inventaire, `swap_vert` pour
// le transfert. A noter, parce que le nom le laisse croire : `Checklist` et
// `ChecklistOutlined` ont le meme trace SVG chez MUI, la variante ne change rien au rendu.
const TABS = [
  { value: INVENTORY_TAB, label: "Inventaire", Icon: ChecklistOutlinedIcon },
  { value: TRANSFER_TAB, label: "Transfert", Icon: SwapVertIcon }
]

const sx = {
  // KFC-353: DSG-36 ne veut pas des onglets soulignes de MUI mais un selecteur segmente —
  // une piste grise arrondie, le segment actif en pastille blanche ombree. Les valeurs sont
  // relevees sur la source du prototype (`td-pntabs` et sa fonction `tabS`), pas sur une
  // capture : une couleur lue a l'oeil sur une image compressee tombe toujours a cote.
  tabs: {
    display: "flex",
    gap: "4px",
    marginTop: "24px",
    padding: "4px",
    background: "#F1F3F4",
    borderRadius: "10px",
  },
  tab: {
    flex: 1,
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    padding: 0,
    border: "none",
    borderRadius: "8px",
    background: "transparent",
    color: "#5f6368",
    boxShadow: "none",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    "& svg": { fontSize: 18 },
  },
  tabSelected: {
    background: "#FFFFFF",
    color: "#202124",
    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
  },
  // KFC-351 : le panneau bloquant de DSG-36 (`showXferBlockInline`). Il REMPLACE le formulaire,
  // il ne s'ajoute pas dessous — un formulaire encore manipulable sous un message « impossible »
  // invite a rejouer l'action qui vient d'etre refusee.
  blockPanel: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: "12px",
    padding: "32px 20px",
  },
  blockBadge: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "#FAE3B4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 28, color: "#7A4E00" },
  },
  blockTitle: {
    fontSize: "20px",
    fontWeight: 600,
    color: "#202124",
  },
  blockSubtitle: {
    fontSize: "16px",
    color: "#5f6368",
  },
  blockLink: {
    marginTop: "8px",
    fontSize: "15px",
    fontWeight: 600,
    color: "#2196F3",
    textTransform: "none",
    "&:hover": { textDecoration: "underline", background: "transparent" },
  },
  // DSG-36 : `showXferAlertInline`, une simple ligne rouge au-dessus du bouton.
  transferAlert: {
    marginTop: "16px",
    fontSize: "16px",
    color: "#F44559",
  },
  brick: {
    marginTop: "20px",
    padding: "20px",
    border: "1px solid #E0E0E0",
    borderRadius: "12px",
  },
  // KFC-285: DSG-36 outlines both bricks when the confirmation carries no entry at all.
  brickInvalid: {
    borderColor: "#D32F2F",
  },
  panelError: {
    marginTop: "20px",
    color: "#D32F2F",
    fontSize: 14,
  },
  entryHint: {
    marginTop: "8px",
    color: "#D32F2F",
    fontSize: 13,
  },
  brickHead: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  badge: {
    flex: "0 0 40px",
    width: 40,
    height: 40,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 22 },
  },
  brickTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#263238",
  },
  brickInput: {
    marginTop: "12px",
    width: "100%",
    "& .MuiInputBase-input": {
      fontSize: 44,
      lineHeight: 1,
      textAlign: "center",
      padding: 0,
    },
  },
  directionCard: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    padding: "14px 8px",
    borderRadius: "10px",
    "& svg": { fontSize: 22 },
  },
  directionTag: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: ".4px",
    textTransform: "uppercase",
  },
  directionLabel: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#263238",
    textAlign: "center",
  },
  swapButton: {
    marginTop: "14px",
    width: "100%",
    height: 44,
    gap: "8px",
    border: "1px solid #DADCE0",
    borderRadius: "10px",
    color: "#2196F3",
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: ".4px",
  },
  quantityLabel: {
    fontSize: "16px",
    color: "#5F6368",
    marginTop: "24px",
    marginBottom: "12px",
  },
  quantityRow: {
    display: "flex",
    alignItems: "stretch",
    height: 52,
    border: "1px solid #DADCE0",
    borderRadius: "10px",
    overflow: "hidden",
  },
  quantityButton: {
    flex: "0 0 56px",
    minWidth: 56,
    borderRadius: 0,
    color: "#5F6368",
  },
  quantityInput: {
    flex: 1,
    // The row stretches this field to the bar's full height, but MUI's input keeps its own
    // natural height and sits at the top of it — the digit ends up 8px above the +/- buttons.
    "& .MuiInputBase-root": { height: "100%" },
    "& .MuiInputBase-input": {
      textAlign: "center",
      fontSize: 18,
      fontWeight: 600,
      color: "#202124",
    },
  },
  cta: {
    marginTop: "28px",
    width: "100%",
    height: 56,
    fontSize: 16,
    fontWeight: 600,
  },
}

/** The fridges the panel shows, in the order the prototype stacks them. */
function visibleFridges(fridgeFilter) {
  if (FRIDGE_FILTERS.RETAIL === fridgeFilter) return [FRIDGES[RETAIL]]
  if (FRIDGE_FILTERS.CLICK_AND_COLLECT === fridgeFilter) return [FRIDGES[CLICK_AND_COLLECT]]

  return [FRIDGES[RETAIL], FRIDGES[CLICK_AND_COLLECT]]
}

/**
 * KFC-191 approach A: the click & collect stock is read from `volume`, not from
 * `volumeClickAndCollect`. The two always carry the same number — they are written together —
 * but only `volume` is present on the documents that predate the approach, which is every
 * document a hub already holds.
 */
const stockOf = (selectedCard, fridgeId) =>
  RETAIL === fridgeId ? selectedCard.volumeLibreService : selectedCard.volume

const InventoryActionPanel = ({ selectedCard, fridgeFilter, onConfirmInventory, onTransfer }) => {
  const [tab, setTab] = useState(INVENTORY_TAB)
  const tabsRef = useRef(null)
  const [volumes, setVolumes] = useState({})
  const [transferQuantity, setTransferQuantity] = useState(1)
  const [swapped, setSwapped] = useState(false)
  // KFC-285: "missing" is the entry-less confirmation, "failed" a write the server refused.
  // They read differently on screen — one asks for a quantity, the other offers a retry.
  const [inventoryError, setInventoryError] = useState(null)
  const [saving, setSaving] = useState(false)
  // The fridge whose last keystroke was refused, so the reason can be shown where it happened.
  const [entryHint, setEntryHint] = useState(null)
  // KFC-351 CA-2 : le pendant de `entryHint` pour la quantite de transfert.
  const [quantityHint, setQuantityHint] = useState(null)
  // KFC-351 : le refus rendu par l'action, et l'echec d'ecriture, qui n'ont pas la meme
  // presentation — le premier remplace le formulaire, le second laisse la saisie et propose
  // de reessayer.
  const [transferBlock, setTransferBlock] = useState(null)
  const [transferError, setTransferError] = useState(false)

  const fridges = visibleFridges(fridgeFilter)
  const isSingleFridge = 1 === fridges.length

  // The prototype reopens on "inventaire" and resets the transfer whenever another product is
  // picked, rather than carrying the previous one's state over.
  useEffect(() => {
    setTab(INVENTORY_TAB)
    setVolumes({})
    setTransferQuantity(1)
    setSwapped(false)
    setInventoryError(null)
    setSaving(false)
    setEntryHint(null)
    // KFC-351 : un refus appartient au produit sur lequel il a ete prononce. Le garder en
    // changeant de tuile afficherait « Transfert impossible » sur un produit parfaitement
    // transferable.
    setTransferBlock(null)
    setTransferError(false)
    setQuantityHint(null)
  }, [selectedCard.objectId])

  /**
   * KFC-285: the entry accepts digits and nothing else, and says so.
   *
   * `pattern` and `inputMode` only hint at the keyboard and at form validation; they stop
   * neither a minus sign, nor a comma, nor a paste. Stripping the offending characters would
   * block them in the letter of the ticket while betraying it in practice: on a tablet the
   * decimal key sits under the thumb and the French separator is a comma, so "2,5" would land
   * silently as 25 — a tenfold error nothing on screen would flag.
   *
   * The keystroke is therefore refused rather than rewritten, and the reason is shown. An empty
   * field stays reachable: "not counted" has to remain distinguishable from "counted zero".
   */
  const setVolume = (fridgeId, value) => {
    const entry = String(value)

    if (!/^\d*$/.test(entry)) {
      // The field is emptied, not merely left as it was. Typing is character by character:
      // refusing only the separator would keep the "2" of "2,5" and then accept the "5",
      // landing 25 — the tenfold error, arrived at one keystroke later. Clearing forces the
      // count to be entered again, and the reason stays on screen until it is.
      setEntryHint(fridgeId)
      setVolumes({ ...volumes, [fridgeId]: "" })

      return
    }

    setEntryHint(null)
    setInventoryError(null)
    setVolumes({ ...volumes, [fridgeId]: entry })
  }

  const entered = (fridgeId) => {
    const value = volumes[fridgeId]

    return value !== undefined && "" !== value
  }

  const confirmInventory = async () => {
    if (saving) return

    // Only the fridges on screen count: a filtered view must not silently record the other one.
    const counts = {}
    fridges.forEach(fridge => {
      if (entered(fridge.id)) counts[fridge.id] = parseInt(volumes[fridge.id], 10)
    })

    if (0 === Object.keys(counts).length) {
      setInventoryError("missing")

      return
    }

    setSaving(true)
    setInventoryError(null)

    const saved = await onConfirmInventory({
      libreService: counts[RETAIL],
      clickAndCollect: counts[CLICK_AND_COLLECT]
    })

    setSaving(false)
    // The entry is kept on failure, so the retry does not ask the counter staff to count again.
    if (!saved) setInventoryError("failed")
  }

  /**
   * In single-fridge view the direction is imposed: out of the fridge on screen, into the
   * other one. In combined view it starts self-service → click & collect and can be swapped.
   */
  const defaultFrom = isSingleFridge ? fridges[0].id : RETAIL
  const from = swapped
    ? (RETAIL === defaultFrom ? CLICK_AND_COLLECT : RETAIL)
    : defaultFrom
  const to = RETAIL === from ? CLICK_AND_COLLECT : RETAIL

  // Bounds read off the prototype: at least one unit when the source holds stock, never more
  // than the source holds.
  const sourceStock = Math.max(0, stockOf(selectedCard, from) || 0)
  const minimumQuantity = sourceStock > 0 ? 1 : 0
  const maximumQuantity = Math.max(minimumQuantity, sourceStock)
  // Un champ vide s'affiche vide — le forcer au minimum masquerait au comptoir manager que sa
  // saisie a ete rejetee. Le bouton est desactive tant qu'il l'est.
  const emptyQuantity = "" === transferQuantity
  const quantity = emptyQuantity ? "" : Math.max(minimumQuantity, Math.min(transferQuantity, maximumQuantity))

  const clampQuantity = (value) => Math.max(minimumQuantity, Math.min(value, maximumQuantity))

  const setQuantity = (value) => {
    const parsed = parseInt(value, 10)

    setTransferQuantity(isNaN(parsed) ? minimumQuantity : clampQuantity(parsed))
  }

  // Stepped from the previous value rather than from the rendered one: two clicks landing in
  // the same React batch would otherwise count as one.
  const stepQuantity = (delta) => setTransferQuantity(previous => {
    // Repartir du minimum quand le champ est vide : « + » sur un champ vide doit donner 1,
    // pas 0 ni NaN.
    const base = "" === previous ? minimumQuantity : clampQuantity(previous)

    setQuantityHint(null)

    return clampQuantity(base + delta)
  })

	/**
	 * Neither a negative nor a decimal value may lead to a transfer. The prototype stops them at
	 * the keystroke rather than refusing them afterwards, so the minus sign, the comma and the
	 * dot never reach the field.
	 */
  const refuseNonDigit = (event) => {
    if (1 === event.key.length && !/[0-9]/.test(event.key) && !event.ctrlKey && !event.metaKey) {
      event.preventDefault()
      // The ticket asks for two distinct messages, so the refused character has to be named:
      // a minus sign is a sign problem, anything else is a decimal problem.
      setQuantityHint("-" === event.key ? "POSITIVE" : "INTEGER")
      setTransferQuantity("")

      return
    }

    setQuantityHint(null)
  }

  const askTransfer = async () => {
    setTransferError(false)

    const outcome = await onTransfer({ from, to, quantity })

    if (TRANSFER_REFUSALS.WRITE_FAILED === outcome) return setTransferError(true)
    if (TRANSFER_REFUSALS.NONE !== outcome) return setTransferBlock(outcome)
  }

  /**
   * DSG-36 date le blocage — « L'inventaire de ce produit a ete valide le 12/07 a 16 h 04. ».
   * On reprend la formulation avec la vraie date du dernier comptage : sans elle, le comptoir
   * manager ne sait pas si le blocage vient de lui ou d'un collegue il y a deux heures.
   */
  const blockSubtitle = () => {
    if (TRANSFER_REFUSALS.NOT_ENOUGH_STOCK === transferBlock) return "Stock insuffisant dans le frigo de départ."

    const inventories = (selectedCard.events || []).filter(event => "INVENTORY" === event.type)
    const last = inventories[inventories.length - 1]

    return last
      ? `L'inventaire a déjà été réalisé, validé le ${moment(last.date).format("DD/MM [à] HH[ h ]mm")}.`
      : "L'inventaire a déjà été réalisé."
  }

  /** Le lien « Modifier la quantite » du prototype : il repart de 1 et rouvre le formulaire. */
  const clearTransferBlock = () => {
    setTransferBlock(null)
    setTransferQuantity(1)
    setQuantityHint(null)
  }

  /**
   * KFC-353 : `<Tabs>` de MUI apportait deux comportements que `role="tablist"` promet et que
   * le selecteur segmente doit donc rendre lui-meme, sans quoi le role annonce un contrat qu'il
   * ne tient pas.
   *
   * Les fleches deplacent le focus dans la piste et bouclent, Home et End vont aux extremites.
   * Comme MUI, elles ne changent pas d'onglet : c'est Entree ou Espace, sur le bouton natif, qui
   * active. Le second comportement est le `tabIndex` tournant plus bas — un seul segment reste
   * dans l'ordre de tabulation, pour qu'une tabulation traverse le groupe entier d'un coup.
   */
  const focusTabAt = (index) => {
    const items = Array.from(tabsRef.current.querySelectorAll("[role=\"tab\"]"))
    const target = items[(index + items.length) % items.length]

    if (target) target.focus()
  }

  const onTabsKeyDown = (event) => {
    const items = Array.from(tabsRef.current.querySelectorAll("[role=\"tab\"]"))
    const current = items.indexOf(document.activeElement)

    if (current < 0) return

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault()
        focusTabAt(current - 1)
        break
      case "ArrowRight":
        event.preventDefault()
        focusTabAt(current + 1)
        break
      case "Home":
        event.preventDefault()
        focusTabAt(0)
        break
      case "End":
        event.preventDefault()
        focusTabAt(items.length - 1)
        break
      default:
        break
    }
  }

  return (
    <Box>
      <Box sx={sx.tabs} role="tablist" ref={tabsRef} onKeyDown={onTabsKeyDown}>
        {TABS.map(({ value, label, Icon }) => (
          <Box
            key={value}
            component="button"
            type="button"
            role="tab"
            aria-selected={value === tab}
            tabIndex={value === tab ? 0 : -1}
            sx={value === tab ? { ...sx.tab, ...sx.tabSelected } : sx.tab}
            onClick={() => setTab(value)}
          >
            <Icon aria-hidden />
            {label}
          </Box>
        ))}
      </Box>

      {INVENTORY_TAB === tab && (
        <Box>
          {fridges.map(fridge => (
            <Box
              key={fridge.id}
              sx={"missing" === inventoryError ? { ...sx.brick, ...sx.brickInvalid } : sx.brick}
            >
              <Box sx={sx.brickHead}>
                <Box sx={{ ...sx.badge, backgroundColor: fridge.background }}>
                  <fridge.Icon aria-hidden sx={{ color: fridge.color }} />
                </Box>
                <Typography sx={sx.brickTitle}>{fridge.label}</Typography>
              </Box>
              <TextField
                name={fridge.id}
                variant="standard"
                sx={sx.brickInput}
                inputProps={{ placeholder: "000", inputMode: "numeric", pattern: "[0-9]*" }}
                value={volumes[fridge.id] ?? ""}
                onChange={(event) => setVolume(fridge.id, event.target.value)}
              />
              {entryHint === fridge.id && (
                <Typography sx={sx.entryHint}>
                  Seuls les nombres entiers sont acceptés.
                </Typography>
              )}
            </Box>
          ))}

          {inventoryError && (
            <Typography sx={sx.panelError}>
              {"missing" === inventoryError
                ? "Veuillez renseigner une quantité dans au moins l'un des deux frigos."
                : "Une erreur est survenue, veuillez réessayer."}
            </Typography>
          )}

          <Button
            sx={sx.cta}
            variant="contained"
            color="primary"
            disableElevation
            disabled={saving}
            onClick={confirmInventory}
          >
            {"failed" === inventoryError ? "Réessayer" : "Confirmer l'inventaire"}
          </Button>
        </Box>
      )}

      {TRANSFER_TAB === tab && transferBlock && (
        <Box sx={sx.blockPanel}>
          <Box sx={sx.blockBadge}>
            <LockIcon aria-hidden />
          </Box>
          <Typography sx={sx.blockTitle}>Transfert impossible</Typography>
          <Typography sx={sx.blockSubtitle}>{blockSubtitle()}</Typography>
          {/*
            * Pas de porte de sortie quand c'est l'inventaire qui bloque : il n'y a rien a
            * corriger, et proposer « Modifier la quantite » laisserait croire le contraire.
            * DSG-36 fait le meme choix — `showXferBlockBtn = blockActive && !invBlocked`.
            */}
          {TRANSFER_REFUSALS.NOT_ENOUGH_STOCK === transferBlock && (
            <Button sx={sx.blockLink} disableRipple onClick={clearTransferBlock}>
              Modifier la quantité
            </Button>
          )}
        </Box>
      )}

      {TRANSFER_TAB === tab && !transferBlock && (
        <Box>
          <Box sx={{ ...sx.brick, marginTop: "20px" }}>
            <Box sx={{ display: "flex", alignItems: "stretch" }}>
              <Box sx={{ ...sx.directionCard, backgroundColor: FRIDGES[from].background }}>
                <Typography sx={{ ...sx.directionTag, color: FRIDGES[from].color }}>Depuis</Typography>
                {React.createElement(FRIDGES[from].Icon, { "aria-hidden": true, sx: { color: FRIDGES[from].color } })}
                <Typography sx={sx.directionLabel}>{FRIDGES[from].label}</Typography>
              </Box>
              <Box sx={{ flex: "0 0 56px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ArrowForwardIcon aria-hidden sx={{ color: "#9AA0A6", fontSize: 26 }} />
              </Box>
              <Box sx={{ ...sx.directionCard, backgroundColor: FRIDGES[to].background }}>
                <Typography sx={{ ...sx.directionTag, color: FRIDGES[to].color }}>Vers</Typography>
                {React.createElement(FRIDGES[to].Icon, { "aria-hidden": true, sx: { color: FRIDGES[to].color } })}
                <Typography sx={sx.directionLabel}>{FRIDGES[to].label}</Typography>
              </Box>
            </Box>

            {/* Single-fridge view imposes the direction, so there is nothing to swap. */}
            {!isSingleFridge && (
              <Button
                sx={sx.swapButton}
                title="Inverser le sens du transfert"
                onClick={() => setSwapped(!swapped)}
              >
                <SwapHorizIcon aria-hidden />
                Inverser le sens
              </Button>
            )}

            <Typography sx={sx.quantityLabel}>Quantité à transférer</Typography>
            <Box sx={sx.quantityRow}>
              <Button
                sx={sx.quantityButton}
                aria-label="Diminuer la quantité"
                disabled={quantity <= minimumQuantity}
                onClick={() => stepQuantity(-1)}
              >
                <RemoveIcon aria-hidden />
              </Button>
              <TextField
                variant="standard"
                sx={sx.quantityInput}
                InputProps={{ disableUnderline: true }}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*", "aria-label": "Quantité à transférer" }}
                value={quantity}
                onKeyDown={refuseNonDigit}
                onChange={(event) => setQuantity(event.target.value)}
              />
              <Button
                sx={sx.quantityButton}
                aria-label="Augmenter la quantité"
                disabled={quantity >= maximumQuantity}
                onClick={() => stepQuantity(1)}
              >
                <AddIcon aria-hidden />
              </Button>
            </Box>
            {quantityHint && (
              <Typography sx={sx.entryHint}>
                {"POSITIVE" === quantityHint
                  ? "Veuillez saisir une valeur positive"
                  : "Veuillez saisir un nombre entier"}
              </Typography>
            )}
          </Box>

          {/*
            * L'echec d'ecriture n'est pas un refus : la saisie reste, le bouton devient
            * « Réessayer », et le message est une simple ligne — pas le panneau bloquant, qui
            * dirait a tort que l'action est impossible alors qu'elle a seulement echoue.
            */}
          {transferError && (
            <Typography sx={sx.transferAlert}>Une erreur est survenue, veuillez réessayer.</Typography>
          )}

          <Button
            sx={{ ...sx.cta, backgroundColor: FRIDGES[to].color, "&:hover": { backgroundColor: FRIDGES[to].color } }}
            variant="contained"
            disableElevation
            disabled={emptyQuantity || !isStockDeclared(stockOf(selectedCard, from)) || 0 === sourceStock}
            onClick={askTransfer}
          >
            {transferError ? "Réessayer" : `Transférer vers le ${FRIDGES[to].transferLabel}`}
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default InventoryActionPanel
